import { useState, useCallback, useMemo, useRef } from 'react';
import { negate, identity } from 'lodash-es';
import { Dropdown as UIDropdown } from '@shopline/dashboard-ui';
import { type IProps as IUIDropdownProps } from '@shopline/dashboard-ui/types/components/dropdown/Dropdown';
import { useTranslation } from 'react-i18next';

import TooltipSelectorWrapper, {
  IProps as ITooltipSelectorWrapperProps,
} from './TooltipSelectorWrapper';

interface IDropdownProps extends IUIDropdownProps {
  shouldShowTooltip?: boolean;
  tooltipDirection?: ITooltipSelectorWrapperProps['tooltipDirection'];
  onClose: () => void;
}

const Dropdown: React.FC<IDropdownProps> = ({
  disabled = false,
  onChange,
  value,
  options,
  placeHolder,
  type = 'singleOption',
  menuAlign = 'left',
  shouldShowTooltip = false,
  tooltipDirection,
  keepPlaceHolder = false,
  onClose,
  ...otherProps
}) => {
  const innerRef = useRef(null);
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleOptionChange = (optionValues: string[]) => {
    onChange(optionValues);
    if (type === 'singleOption') handleDropdownClose();
  };

  const handleDropdownClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const onDropdownClick = useCallback(() => {
    if (disabled) return;
    setIsOpen(negate(identity<boolean>));
  }, [disabled]);

  const noResultOption = useMemo(
    () => ({
      key: 'No Results',
      value: 'No Results',
      label: t('No Results'),
    }),
    [t],
  );

  const handleValueToLabel = useCallback(
    (optionValue: string) => {
      const currentOption = options.find(
        (option) => option.value === optionValue,
      );
      if (currentOption) {
        return `${currentOption.label}`;
      }
      return '';
    },
    [options],
  );

  const currentLabel = useMemo(
    () =>
      !value.length || keepPlaceHolder
        ? placeHolder
        : value.map((selected) => handleValueToLabel(selected)).join(','),
    [handleValueToLabel, value, keepPlaceHolder, placeHolder],
  );

  const renderTooltipSelector = ({
    renderDefaultSelector,
  }: {
    renderDefaultSelector: () => React.ReactNode;
  }) => (
    <TooltipSelectorWrapper
      width={otherProps.width}
      dropdownRef={innerRef}
      tooltipDirection={tooltipDirection}
      text={currentLabel}
    >
      {renderDefaultSelector()}
    </TooltipSelectorWrapper>
  );

  return (
    <UIDropdown
      disabled={disabled}
      ref={innerRef}
      type={type}
      options={options}
      isOpen={isOpen}
      value={value}
      placeHolder={placeHolder}
      onClick={onDropdownClick}
      onClose={handleDropdownClose}
      onChange={handleOptionChange}
      menuAlign={menuAlign}
      keepPlaceHolder={keepPlaceHolder}
      optionPlaceholder={noResultOption}
      {...(shouldShowTooltip && { renderSelector: renderTooltipSelector })}
      {...otherProps}
    />
  );
};

export default Dropdown;
export { default as LoadingDropdownSelector } from './LoadingDropdownSelector';
